import React, { useEffect, useState } from "react";
import { Drawer, Tree, Input, Space, Button, Spin, Typography, Divider, message, Pagination } from "antd";
import type { DataNode } from "antd/es/tree";
import { listDeviceGroups } from "@/api/devicegroupService";
import { MediaMTXBroadcast, MediaMTXUpdateBroadcast, MediaSoupBroadcast } from "@/api/broadcastService";
import { mediasoupService } from "@/mediaservices/mediasoupService";
import { BroadcastStorage } from "@/utils/BroadcastStorage";
import { mediamtxService } from "@/mediaservices/mediamtxService";

const { Search } = Input;
const { Text } = Typography;

export type Key = string; // "group-123" | "device-<groupId>-<deviceId>"

interface Props {
  open: boolean;
  state: "stopped" | "live" | "paused";
  selectedKeys: Key[];
  broadcastName?: string; // ✅ Add this prop
  onClose: () => void;

  // When stopped: clicking primary starts the broadcast with current selection
  onStartBroadcast: (selectedKeys: Key[]) => void;

  // When live/paused: clicking primary updates audience
  onUpdateAudience: (selectedKeys: Key[]) => void;
}

// API response models (minimal local types)
type ApiDevice = {
  id: number;
  device_serial?: string;
  device_name?: string;
  location?: string;
  firmware_version?: string;
};
type ApiGroup = {
  id: number;
  name: string;
  devices: ApiDevice[];
};

// Extend antd DataNode to carry extra info we need
type DeviceNode = DataNode & {
  kind?: "group" | "device";
  serial?: string; // deviceSerial for API payload
};

const USE_MEDIAMTX = String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === "true";

const AudienceSelectorDrawer: React.FC<Props> = ({
  open,
  state,
  selectedKeys,
  broadcastName,
  onClose,
  onStartBroadcast,
  onUpdateAudience,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalGroups, setTotalGroups] = useState(0);
  
  const [treeData, setTreeData] = useState<DeviceNode[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<Key[]>(selectedKeys);
  
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [broadcastNameInput, setBroadcastNameInput] = useState("");

  // Get current host and co-host roles
  const getCurrentRoleDevices = () => {
    if (USE_MEDIAMTX) {
      const { hostId, coHostId } = mediamtxService.getCurrentRoles();
      return { hostSerial: hostId, coHostSerial: coHostId };
    } else {
      const { hostId, coHostId } = mediasoupService.getCurrentRoles();
      return { hostSerial: hostId, coHostSerial: coHostId };
    }
  };

  const getCurrentRoleNames = () => {
    const { hostName, coHostName } = mediamtxService.getCurrentRoleNames();
    return {
      hostName: hostName,
      coHostName: coHostName,
    };
  }

  // Get keys for host/co-host devices from tree data
  const getRoleDeviceKeys = (nodes: DeviceNode[]) => {
    const { hostSerial, coHostSerial } = getCurrentRoleDevices();
    const deviceNodes = flattenDevices(nodes);
    const roleKeys: Key[] = [];
    
    deviceNodes.forEach((node) => {
      if (node.serial && (node.serial === hostSerial || node.serial === coHostSerial)) {
        roleKeys.push(String(node.key));
      }
    });
    
    return roleKeys;
  };

  // Auto-select role devices and merge with existing selection
  const autoSelectRoleDevices = (currentKeys: Key[], nodes: DeviceNode[]) => {
    const roleKeys = getRoleDeviceKeys(nodes);
    const mergedKeys = [...new Set([...currentKeys, ...roleKeys])];
    return mergedKeys;
  };

  useEffect(() => {
    // Only update when drawer opens for the first time
    if (open && checkedKeys.length === 0) {
      setCheckedKeys(selectedKeys);
    }
  }, [open]);
  useEffect(() => {
    if (open && treeData.length > 0) {
      let keysToSelect: Key[] = [];
      
      if (state !== "stopped") {
        // Get currently selected devices from storage (more reliable than props)
        const currentDevices = BroadcastStorage.getSelectedDevices() || [];
        // Find matching tree keys for these device serials
        const deviceNodes = flattenDevices(treeData);
        
        deviceNodes.forEach((node) => {
          if (node.serial && currentDevices.includes(node.serial)) {
            keysToSelect.push(String(node.key));
          }
        });
      } else {
        // When starting new broadcast, use the current checked keys as base
        keysToSelect = [...checkedKeys];
      }
      
      // ✅ Always auto-select host and co-host devices and ensure they're included
      const finalKeys = autoSelectRoleDevices(keysToSelect, treeData);
      
      setCheckedKeys(finalKeys);
    }
  }, [open, state, treeData]); // ✅ Removed selectedKeys dependency here too

  // Reset when drawer opens
  useEffect(() => {
    if (open && state === "stopped") {
      setBroadcastNameInput("");
      setCurrentPage(1);
      setSearchInput("");
      setAppliedSearch("");
    } else if (open) {
      // Updating existing broadcast - use current name (read-only)
      setBroadcastNameInput(broadcastName || "");
    }
  }, [open, state, broadcastName]);

  // Load device groups with applied search
  useEffect(() => {
    async function load() {
      if (!open) return;
      setLoading(true);
      try {
        const response = await listDeviceGroups({
          page: currentPage,
          limit: pageSize,
          q: appliedSearch.trim() || undefined,
        });
        
        setTotalGroups(response.pagination?.totalRecords || 0);

        const nodes: DeviceNode[] = (response.data || []).map((g) => ({
          key: `group-${g.id}`,
          title: g.name,
          selectable: false,
          kind: "group",
          children: ((g.devices || []) as ApiDevice[]).map((d) => {
            const deviceKey = `device-${g.id}-${d.id}`;
            // Use device_name if available, fallback to device_serial
            const displayName = d.device_name || d.device_serial || `Device #${d.id}`;
            const labelParts = [displayName, d.location].filter(Boolean);
            const deviceTitle = labelParts.join(" • ");
            
            // ✅ Add role indicators to device titles and disable role devices
            const { hostSerial, coHostSerial } = getCurrentRoleDevices();
            let enhancedTitle = deviceTitle;
            let disabled = false;
            
            if (d.device_serial === hostSerial) {
              enhancedTitle = `${deviceTitle} (Host)`;
              disabled = true;
            } else if (d.device_serial === coHostSerial) {
              enhancedTitle = `${deviceTitle} (Co-Host)`;
              disabled = true;
            }
            
            const node: DeviceNode = {
              key: deviceKey,
              title: enhancedTitle,
              kind: "device",
              serial: d.device_serial,
              disabled, // ✅ Disable role devices
            };
            return node;
          }),
        }));

        setTreeData(nodes);
        
      } catch (err: any) {
        console.error(err);
        message.error("Failed to load device groups");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [open, currentPage, pageSize, appliedSearch]);

  // ✅ Handle search button click
  const handleSearch = () => {
    setAppliedSearch(searchInput);
    setCurrentPage(1); // Reset to page 1 when searching
  };

  // ✅ Handle clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const displayData = treeData;

  // Helpers to collect selected device serials from checkedKeys
  const flattenDevices = (nodes: DeviceNode[]): DeviceNode[] => {
    const out: DeviceNode[] = [];
    const walk = (arr: DeviceNode[]) => {
      for (const n of arr) {
        if (n.kind === "device") out.push(n);
        const ch = (n.children as DeviceNode[] | undefined) ?? [];
        if (ch.length) walk(ch);
      }
    };
    walk(nodes);
    return out;
  };

  const selectedDeviceSerials = (keys: Key[], nodes: DeviceNode[]) => {
    const deviceNodes = flattenDevices(nodes);
    const byKey = new Map<string, DeviceNode>(deviceNodes.map((n) => [String(n.key), n]));
    const serials: string[] = [];
    keys.forEach((k) => {
      const dn = byKey.get(String(k));
      if (dn?.serial) serials.push(dn.serial);
    });
    // De-dup just in case
    return Array.from(new Set(serials));
  };

  const primaryLabel = state === "stopped" ? "Start Broadcast" : "Update Audience";

  // ✅ Modified onCheck handler to prevent unchecking role devices
  const handleTreeCheck = (keys: any) => {
    const newKeys = keys as Key[];
    const roleKeys = getRoleDeviceKeys(treeData);
    
    // ✅ Always ensure role devices remain selected
    const finalKeys = autoSelectRoleDevices(newKeys, treeData);
    
    setCheckedKeys(finalKeys);
  };

  // ✅ Modified clear function to preserve role devices
  const handleClear = () => {
    const roleKeys = getRoleDeviceKeys(treeData);
    setCheckedKeys(roleKeys); // Only keep role devices
  };

  const onPrimary = async () => {
    // Starting a new broadcast requires a name
    if (state === "stopped" && !broadcastNameInput.trim()) {
      message.warning("Please enter a broadcast name.");
      return;
    }

    // Map selection -> device serials
    const serials = selectedDeviceSerials(checkedKeys, treeData);

    if (state === "stopped") {
      // STARTING NEW BROADCAST - use broadcastNameInput
      if (serials.length === 0) {
        message.warning("Please select at least one device.");
        return;
      }

      try {
        setSubmitting(true);

        if (USE_MEDIAMTX) {
          const { hostId, coHostId } = mediamtxService.getCurrentRoles();

          const payload = {
            streamName: broadcastNameInput.trim(),
            description: "test",
            devices: serials.map((s) => ({
              deviceSerial: s,
              isHost: s === hostId,
              isCoHost: s === coHostId,
            })),
            hostDeviceSerial: hostId || "",
            coHostDeviceSerial: coHostId || "",
          };

          const response = await MediaMTXBroadcast(payload);
          
          BroadcastStorage.storeSelectedDevices(serials);
          BroadcastStorage.storeBroadcastName(broadcastNameInput.trim());
          BroadcastStorage.storeStreamId(response.streamId);
          
        } else {
          // MediaSoup logic for starting broadcast
          const { hostId, coHostId } = mediasoupService.getCurrentRoles();
          const hostProducerIds = mediasoupService.getHostProducerIds();
          const coHostProducerIds = mediasoupService.getCoHostProducerIds();

          const payload = {
            streamName: broadcastNameInput.trim(),
            description: "test",
            devices: serials,
            hostDeviceSerial: {
              deviceSerial: hostId || "",
              audioProducerId: hostProducerIds[0] || "",
              videoProducerId: hostProducerIds[1] || "",
            },
            coHostDeviceSerial: [{
              deviceSerial: coHostId || "",
              audioProducerId: coHostProducerIds[0] || "",
              videoProducerId: coHostProducerIds[1] || "",
            }],
          };

          const response = await MediaSoupBroadcast(payload);
          
          BroadcastStorage.storeSelectedDevices(serials);
          BroadcastStorage.storeBroadcastName(broadcastNameInput.trim());
          BroadcastStorage.storeStreamId(response.streamId);
        }

        message.success("Broadcast started");
        onStartBroadcast(checkedKeys);
      } catch (e: any) {
        console.error(e);
        message.error(e?.message || "Failed to start broadcast");
      } finally {
        setSubmitting(false);
      }
    } else {
      // ✅ UPDATING AUDIENCE - Fixed to handle add/update/remove actions
      try {
        setSubmitting(true);

        if (USE_MEDIAMTX) {
          const streamId = BroadcastStorage.getStreamId();
          if (!streamId) {
            message.error("No active stream found");
            return;
          }

          // ✅ Get currently selected devices vs previously stored devices
          let newSelectedSerials = selectedDeviceSerials(checkedKeys, treeData);
          const previouslySelectedSerials = BroadcastStorage.getSelectedDevices() || [];
          const { hostId, coHostId } = mediamtxService.getCurrentRoles();

          // ✅ Ensure host/co-host are in the selection BEFORE calculating changes
          if (hostId && !newSelectedSerials.includes(hostId)) {
            newSelectedSerials.push(hostId);
          }
          if (coHostId && !newSelectedSerials.includes(coHostId)) {
            newSelectedSerials.push(coHostId);
          }

          // ✅ Calculate device changes
          const addedDevices = newSelectedSerials.filter(serial => 
            !previouslySelectedSerials.includes(serial)
          );
          const removedDevices = previouslySelectedSerials.filter(serial => 
            !newSelectedSerials.includes(serial)
          );
          const existingDevices = newSelectedSerials.filter(serial => 
            previouslySelectedSerials.includes(serial)
          );
          
          // ✅ Build payload with proper actions
          const devices = [];

          // Add new devices with action: "add"
          addedDevices.forEach(serial => {
            devices.push({
              deviceSerial: serial,
              isHost: serial === hostId,
              isCoHost: serial === coHostId,
              action: 'add' as const,
            });
          });

          // Update existing devices with action: "update"
          existingDevices.forEach(serial => {
            devices.push({
              deviceSerial: serial,
              isHost: serial === hostId,
              isCoHost: serial === coHostId,
              action: 'update' as const,
            });
          });

          // Remove devices with action: "remove"
          removedDevices.forEach(serial => {
            devices.push({
              deviceSerial: serial,
              isHost: false, // Removed devices can't be host
              isCoHost: false, // Removed devices can't be co-host
              action: 'remove' as const,
            });
          });

          const payload = {
            streamName: broadcastName || BroadcastStorage.getBroadcastName() || "Updated Broadcast",
            devices: devices,
            newHostDeviceSerial: hostId || undefined,
          };

          // ✅ Validation: Ensure we have at least one device that's not being removed
          const remainingDevices = devices.filter(d => d.action !== 'remove');
          if (remainingDevices.length === 0) {
            message.warning("Cannot remove all devices from broadcast. At least one device must remain.");
            return;
          }

          const response = await MediaMTXUpdateBroadcast(streamId, payload);
          
          // ✅ Update local storage with new device selection (only devices that weren't removed)
          BroadcastStorage.storeSelectedDevices(newSelectedSerials);
          
          // ✅ Show detailed success message
          if (response.success) {
            const changes = response.changes;
            const changeMessages = [];
            
            if (addedDevices.length > 0) {
              changeMessages.push(`${addedDevices.length} devices added`);
            }
            if (removedDevices.length > 0) {
              changeMessages.push(`${removedDevices.length} devices removed`);
            }
            if (existingDevices.length > 0) {
              changeMessages.push(`${existingDevices.length} devices updated`);
            }

            const successMsg = changeMessages.length > 0
              ? `Audience updated • ${changeMessages.join(", ")}`
              : `Audience updated`;

            message.success(successMsg);
          }
          
        } else {
          // ✅ MediaSoup update logic remains the same for now
          const { hostId, coHostId } = mediasoupService.getCurrentRoles();
          const hostProducerIds = mediasoupService.getHostProducerIds();
          const coHostProducerIds = mediasoupService.getCoHostProducerIds();

          const serials = selectedDeviceSerials(checkedKeys, treeData);

          const payload = {
            streamName: broadcastName || BroadcastStorage.getBroadcastName() || "Updated Broadcast",
            description: "Audience update",
            devices: serials,
            hostDeviceSerial: {
              deviceSerial: hostId || "",
              audioProducerId: hostProducerIds[0] || "",
              videoProducerId: hostProducerIds[1] || "",
            },
            coHostDeviceSerial: [{
              deviceSerial: coHostId || "",
              audioProducerId: coHostProducerIds[0] || "",
              videoProducerId: coHostProducerIds[1] || "",
            }],
          };

          const response = await MediaSoupBroadcast(payload);
          BroadcastStorage.storeStreamId(response.streamId);
          BroadcastStorage.storeSelectedDevices(serials);
          message.success(`Audience updated • ${serials.length} devices selected`);
        }

        onUpdateAudience(checkedKeys);
      } catch (e: any) {
        console.error('❌ Failed to update audience:', e);
        message.error(e?.message || "Failed to update audience");
      } finally {
        setSubmitting(false);
      }
    }
  };


  const primaryDisabled = state === "stopped" ? (!broadcastNameInput.trim() || submitting) : submitting;

  return (
    <Drawer
      title={state === "stopped" ? "Choose audience before starting the broadcast" : "Manage Audience"}
      placement="right"
      width={420}
      onClose={onClose}
      open={open}
      destroyOnClose
    >
      {/* ✅ Show current broadcast name when updating */}
      {state !== "stopped" && broadcastName && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: 'var(--color-primary-bg)', border: '1px solid var(--color-primary-border)', borderRadius: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 500, color: 'var(--color-primary)' }}>
              Updating: {broadcastName}
            </span>
          </div>
        </div>
      )}

      {/* ✅ Show role device info */}
      {(() => {
        const { hostName, coHostName } = getCurrentRoleNames();
        if (hostName || coHostName) {
          return (
            <div style={{ marginBottom: 12, padding: 8, backgroundColor: '#0000', borderRadius: 4, fontSize: '12px' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Auto-selected devices:</div>
              {hostName && <div> Host: {hostName}</div>}
              {coHostName && <div> Co-Host: {coHostName}</div>}
              <div style={{ fontStyle: 'italic', opacity: 0.8, marginTop: 4 }}>
                Host and Co-Host devices are automatically included and cannot be removed.
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <Space wrap>
          <Button onClick={handleClear} disabled={submitting}>
            Clear
          </Button>
          <Button type="primary" onClick={onPrimary} disabled={primaryDisabled} loading={submitting}>
            {primaryLabel}
          </Button>
        </Space>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      {/* ✅ Only show broadcast name input when starting new broadcast */}
      {state === "stopped" && (
        <>
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            <Text strong>Broadcast name</Text>
            <Input
              placeholder="e.g., Town Hall – West Zone"
              value={broadcastNameInput}
              onChange={(e) => setBroadcastNameInput(e.target.value)}
              maxLength={80}
              showCount
              allowClear
              disabled={submitting}
            />
          </Space>
          <Divider style={{ margin: "12px 0" }} />
        </>
      )}

      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {/* Search controls */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Input
            placeholder="Search Group Name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            disabled={loading || submitting}
            onPressEnter={handleSearch}
            style={{ flex: 1 }}
          />
          <Button 
            onClick={handleSearch}
            disabled={loading || submitting}
            type="primary"
          >
            Search
          </Button>
          {appliedSearch && (
            <Button 
              onClick={handleClearSearch}
              disabled={loading || submitting}
            >
              Clear
            </Button>
          )}
        </div>
        
        {/* Show current search query if any */}
        {appliedSearch && (
          <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            Search results for: "{appliedSearch}"
          </div>
        )}
        
        {/* ✅ Pagination without pageSizeOptions (uses default) */}
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalGroups}
          showSizeChanger
          onChange={(page, size) => {
            setCurrentPage(page);
            if (size !== pageSize) {
              setPageSize(size);
              setCurrentPage(1);
            }
          }}
          size="small"
          disabled={loading || submitting}
        />

        <Spin spinning={loading}>
          <Tree
            checkable
            selectable={false}
            treeData={displayData}
            checkedKeys={checkedKeys}
            onCheck={handleTreeCheck}
            defaultExpandAll
            disabled={submitting}
          />
        </Spin>
      </Space>
    </Drawer>
  );
};

export default AudienceSelectorDrawer;
